/**
 * Aggressive Patrol Behavior
 * 
 * A state-machine based NPC behavior with three states:
 * - PATROL: Walk back and forth within patrol bounds
 * - CHASE: Move toward player when detected but out of attack range
 * - ATTACK: Attack player when in range, with cooldown between attacks
 * 
 * State transitions:
 * - PATROL → CHASE: Player enters detection range
 * - CHASE → ATTACK: Player within attack range and cooldown ready
 * - ATTACK → CHASE: Attack on cooldown and player out of attack range
 * - CHASE → PATROL: Player leaves detection range
 * - ATTACK → PATROL: Player leaves detection range
 */

import type { NPCBehavior, NPCBehaviorConfig } from './NPCBehavior';
import type { AITickAPI } from '../hooks/NPCHookAPI';
import { defaultDamage, defaultDeath } from '../presets/combat';

// ============================================================================
// STATE MACHINE TYPES
// ============================================================================

/** Possible AI states */
export type AIState = 'patrol' | 'chase' | 'attack';

/** Configuration for the state machine behavior */
export interface AggressivePatrolConfig {
  /** Attack range in meters */
  attackRange: number;
  /** Detection range in meters */
  detectionRange: number;
  /** Cooldown between attacks in seconds */
  attackCooldown: number;
  /** Patrol bounds (T values on spline) */
  patrolMinT: number;
  patrolMaxT: number;
}

/** Internal state for the behavior */
interface BehaviorState {
  currentState: AIState;
  patrolDirection: number; // 1 = right, -1 = left
  attackCooldownRemaining: number;
  isAttacking: boolean; // Currently in attack animation
  attackLockTimer: number; // Time remaining in attack animation
}

// ============================================================================
// STATE HANDLERS
// ============================================================================

const ATTACK_LOCK_DURATION = 0.8; // Seconds to lock movement after attack starts

/**
 * PATROL state: Walk back and forth within patrol bounds
 */
function handlePatrolState(
  api: AITickAPI,
  config: AggressivePatrolConfig,
  state: BehaviorState
): AIState {
  // Check for state transition: player detected
  if (api.playerDistance <= config.detectionRange) {
    console.log(`[AI] PATROL → CHASE: Player detected at ${api.playerDistance.toFixed(2)}m`);
    return 'chase';
  }
  
  // Patrol logic: reverse at bounds
  if (api.npcT >= config.patrolMaxT && state.patrolDirection > 0) {
    state.patrolDirection = -1;
  } else if (api.npcT <= config.patrolMinT && state.patrolDirection < 0) {
    state.patrolDirection = 1;
  }
  
  api.actions.setMovement(state.patrolDirection);
  api.actions.setFacing(state.patrolDirection);
  
  return 'patrol';
}

/**
 * CHASE state: Move toward player until in attack range
 */
function handleChaseState(
  api: AITickAPI,
  config: AggressivePatrolConfig,
  state: BehaviorState
): AIState {
  // Tick cooldown
  state.attackCooldownRemaining = Math.max(0, state.attackCooldownRemaining - api.deltaTime);
  
  // Check for state transitions
  if (api.playerDistance > config.detectionRange) {
    console.log(`[AI] CHASE → PATROL: Player lost at ${api.playerDistance.toFixed(2)}m`);
    return 'patrol';
  }
  
  if (api.playerDistance <= config.attackRange && state.attackCooldownRemaining <= 0) {
    console.log(`[AI] CHASE → ATTACK: Player in range at ${api.playerDistance.toFixed(2)}m`);
    // Trigger initial attack immediately on state entry
    state.isAttacking = true;
    state.attackCooldownRemaining = config.attackCooldown;
    state.attackLockTimer = ATTACK_LOCK_DURATION;
    api.actions.setAttack(true);
    console.log(`[AI] ATTACK: Initial attack at ${api.playerDistance.toFixed(2)}m`);
    return 'attack';
  }
  
  // Chase logic: move toward player
  const directionToPlayer = api.playerDirection;
  api.actions.setMovement(directionToPlayer);
  api.actions.setFacing(directionToPlayer);
  
  return 'chase';
}

/**
 * ATTACK state: Attack player, then transition based on range
 */
function handleAttackState(
  api: AITickAPI,
  config: AggressivePatrolConfig,
  state: BehaviorState
): AIState {
  // Tick timers
  state.attackCooldownRemaining = Math.max(0, state.attackCooldownRemaining - api.deltaTime);
  state.attackLockTimer = Math.max(0, state.attackLockTimer - api.deltaTime);
  
  // Face player always in attack state
  api.actions.setFacing(api.playerDirection);
  api.actions.setMovement(0); // Don't move while attacking
  
  // Check for state transitions (only after attack lock ends)
  if (state.attackLockTimer <= 0) {
    // Clear attack input after lock ends - but DON'T immediately start new attack
    // We need at least one frame with attack=false for rising edge detection to work
    if (state.isAttacking) {
      state.isAttacking = false;
      api.actions.setAttack(false);
      // Return early - next frame we can check if we should attack again
      return 'attack';
    }
    
    if (api.playerDistance > config.detectionRange) {
      console.log(`[AI] ATTACK → PATROL: Player lost at ${api.playerDistance.toFixed(2)}m`);
      return 'patrol';
    }
    
    if (api.playerDistance > config.attackRange) {
      console.log(`[AI] ATTACK → CHASE: Player out of attack range at ${api.playerDistance.toFixed(2)}m`);
      return 'chase';
    }
    
    // Player still in attack range - attack again if cooldown ready
    if (state.attackCooldownRemaining <= 0) {
      // Trigger new attack
      state.isAttacking = true;
      state.attackCooldownRemaining = config.attackCooldown;
      state.attackLockTimer = ATTACK_LOCK_DURATION;
      api.actions.setAttack(true);
      console.log(`[AI] ATTACK: Attacking player at ${api.playerDistance.toFixed(2)}m`);
    }
  }
  
  return 'attack';
}

// ============================================================================
// BEHAVIOR FACTORY
// ============================================================================

/**
 * Create an aggressive patrol behavior with proper state machine.
 * 
 * @param config - Configuration from map annotations
 * @returns NPCBehavior instance with closure state
 */
export const createAggressivePatrol = (config: NPCBehaviorConfig): NPCBehavior => {
  // Calculate T bounds from meters
  const splineLength = config.splineLength ?? 10;
  const spawnT = config.spawnT ?? 0.5;
  
  const leftT = spawnT - (config.patrolLeft ?? 2) / splineLength;
  const rightT = spawnT + (config.patrolRight ?? 2) / splineLength;
  
  // Build config
  const behaviorConfig: AggressivePatrolConfig = {
    attackRange: config.attackRange ?? 2.0,
    detectionRange: config.detectionRange ?? 10.0,
    attackCooldown: config.attackCooldown ?? 1.5,
    patrolMinT: Math.max(0, leftT),
    patrolMaxT: Math.min(1, rightT),
  };
  
  console.log(`[AggressivePatrol] Config:`, behaviorConfig);
  
  // Initialize state
  const state: BehaviorState = {
    currentState: 'patrol',
    patrolDirection: 1,
    attackCooldownRemaining: 0,
    isAttacking: false,
    attackLockTimer: 0,
  };
  
  // State handler dispatch table
  const stateHandlers: Record<AIState, (api: AITickAPI) => AIState> = {
    patrol: (api) => handlePatrolState(api, behaviorConfig, state),
    chase: (api) => handleChaseState(api, behaviorConfig, state),
    attack: (api) => handleAttackState(api, behaviorConfig, state),
  };
  
  let tickCount = 0;
  
  return {
    id: 'aggressive_patrol',
    displayName: 'Aggressive Patrol',
    
    onTick: (api) => {
      tickCount++;
      
      // Run current state handler, get next state
      const handler = stateHandlers[state.currentState];
      const nextState = handler(api);
      
      // Log state periodically
      if (tickCount % 120 === 0) {
        console.log(`[AggressivePatrol] state=${state.currentState}, npcT=${api.npcT.toFixed(4)}, playerDist=${api.playerDistance.toFixed(2)}, cooldown=${state.attackCooldownRemaining.toFixed(2)}`);
      }
      
      // Transition if state changed
      if (nextState !== state.currentState) {
        state.currentState = nextState;
      }
    },
    
    // Detection events are now handled internally by state machine
    // These hooks are kept for compatibility but state machine handles transitions
    onPlayerDetected: (_api) => {
      // State machine handles this via distance check
    },
    
    onPlayerLost: (_api) => {
      // State machine handles this via distance check
    },
    
    onDamage: defaultDamage,
    
    onDeath: defaultDeath,
    
    onSpawn: (_api) => {
      console.log(`[AggressivePatrol] onSpawn - resetting state`);
      state.currentState = 'patrol';
      state.patrolDirection = 1;
      state.attackCooldownRemaining = 0;
      state.isAttacking = false;
      state.attackLockTimer = 0;
    },
  };
};
